/**
 * How well someone knows you.
 *
 * Points earned by turning up and by giving things, spent on nothing —
 * friendship is not a currency. What it buys is that people behave
 * differently, which is a thing the world does rather than a thing you own.
 */

export interface FriendshipTier {
  readonly name: string;
  /** Points at which this tier begins. */
  readonly from: number;
  /** Fraction off the merchant's prices, for those who trade. */
  readonly discount: number;
}

/**
 * Ordered from strangers upward. The last tier whose threshold is met wins,
 * so adding one in the middle needs no other change.
 */
export const FRIENDSHIP_TIERS: readonly FriendshipTier[] = [
  { name: 'Stranger', from: 0, discount: 0 },
  { name: 'Acquaintance', from: 80, discount: 0.05 },
  { name: 'Friend', from: 240, discount: 0.12 },
  { name: 'Confidant', from: 500, discount: 0.2 },
];

/** What a gift is worth. */
export const GIFT_POINTS = {
  liked: 45,
  neutral: 18,
  disliked: -25,
} as const;

/** Turning up matters, but only the first time each day. */
export const TALK_POINTS = 3;

/**
 * Gifts per person per day.
 *
 * One. A limit is what makes a gift a gesture rather than a transaction, and
 * without it the whole system is "hand over forty turnips".
 */
export const GIFTS_PER_DAY = 1;

/** Points cannot fall below this, so a bad gift is a setback, not a ruin. */
export const FRIENDSHIP_FLOOR = 0;

export function tierFor(points: number): FriendshipTier {
  let tier = FRIENDSHIP_TIERS[0]!;
  for (const candidate of FRIENDSHIP_TIERS) {
    if (points >= candidate.from) tier = candidate;
  }
  return tier;
}
